import type { FC, FormEvent, ReactNode } from 'react'
import type { AcademicYear, Semester } from '@/types'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/dates'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/feedback'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'

import {
    ArrowRight,
    CalendarDays,
    CalendarRange,
    Check,
    Pencil,
    Plus,
    ShieldCheck,
    Trash2
} from 'lucide-react'

type FormState = {
    id: string | null
    name: string
    semester: Semester
    startDate: string
    endDate: string
}

const EMPTY_FORM: FormState = { id: null, name: '', semester: '1', startDate: '', endDate: '' }

const SchoolYearsPage: FC = (): ReactNode => {
    const { data, currentUser, actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()
    const formRef = useRef<HTMLFormElement>(null)
    const nameRef = useRef<HTMLInputElement>(null)

    const [form, setForm] = useState<FormState>(EMPTY_FORM)

    if (currentUser.role !== 'admin') {
        return (
            <Card>
                <EmptyState icon={<ShieldCheck />} title='Khusus admin' description='Halaman tahun ajaran hanya dapat dibuka oleh akun dengan peran admin.' />
            </Card>
        )
    }

    const editing = form.id !== null

    const groups = Array.from(
        [...data.years]
            .sort((a, b) => b.name.localeCompare(a.name) || a.semester.localeCompare(b.semester))
            .reduce((map, year) => map.set(year.name, [...(map.get(year.name) ?? []), year]), new Map<string, AcademicYear[]>())
    )

    const startEdit = (year: AcademicYear): void => {
        setForm({ id: year.id, name: year.name, semester: year.semester, startDate: year.startDate ?? '', endDate: year.endDate ?? '' })
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        window.setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 50)
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveYear({
            id: form.id ?? undefined,
            name: form.name,
            semester: form.semester,
            startDate: form.startDate || null,
            endDate: form.endDate || null
        })

        if (result.ok) {
            toast.success(result.message ?? 'Tahun ajaran disimpan.')
            setForm(EMPTY_FORM)
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleActivate = (year: AcademicYear): void => {
        const result = actions.activateYear(year.id)

        if (result.ok) toast.success(result.message ?? 'Tahun ajaran dijadikan aktif.')
        else toast.error(result.error)
    }

    const handleDelete = async (year: AcademicYear): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus semester ini?',
            description: `Semester ${year.semester} tahun ajaran ${year.name} akan dihapus.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        const result = actions.deleteYear(year.id)

        if (result.ok) toast.success(result.message ?? 'Tahun ajaran dihapus.')
        else toast.error('Gagal menghapus', result.error)

        if (form.id === year.id) setForm(EMPTY_FORM)
    }

    return (
        <>
            <Seo title='Tahun ajaran' />

            <PageHeader
                eyebrow='Administrasi'
                title='Tahun Ajaran & Semester'
                description='Setiap tahun ajaran otomatis mencakup Semester 1 dan Semester 2.'
            />

            <div className='grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]'>
                <Card className={cn('lg:sticky lg:top-24', editing && 'ring-2 ring-primary/30')}>
                    <CardHeader
                        eyebrow='Formulir'
                        title={editing ? 'Ubah tahun ajaran' : 'Tambah tahun ajaran'}
                        icon={editing ? <Pencil /> : <Plus />}
                    />

                    <form ref={formRef} onSubmit={handleSubmit} className='space-y-4 p-5'>
                        <Field label='Nama tahun ajaran' htmlFor='yearName'>
                            <Input
                                ref={nameRef}
                                id='yearName'
                                value={form.name}
                                onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                                placeholder='Contoh: 2026/2027'
                                maxLength={30}
                                required
                            />
                        </Field>

                        {editing && (
                            <>
                                <Field label='Semester' htmlFor='yearSemester'>
                                    <Select
                                        id='yearSemester'
                                        value={form.semester}
                                        onChange={event => setForm(current => ({ ...current, semester: event.target.value as Semester }))}
                                        required
                                    >
                                        <option value='1'>Semester 1</option>
                                        <option value='2'>Semester 2</option>
                                    </Select>
                                </Field>

                                <div className='grid grid-cols-2 gap-3'>
                                    <Field label='Tanggal mulai' htmlFor='yearStart' optional>
                                        <Input id='yearStart' type='date' value={form.startDate} onChange={event => setForm(current => ({ ...current, startDate: event.target.value }))} />
                                    </Field>

                                    <Field label='Tanggal selesai' htmlFor='yearEnd' optional>
                                        <Input id='yearEnd' type='date' value={form.endDate} onChange={event => setForm(current => ({ ...current, endDate: event.target.value }))} />
                                    </Field>
                                </div>
                            </>
                        )}

                        <div className='flex gap-2 pt-1'>
                            <Button type='submit' className='flex-1'>
                                <Check aria-hidden='true' />
                                {editing ? 'Simpan perubahan' : 'Simpan tahun ajaran'}
                            </Button>

                            <Button variant='secondary' onClick={() => setForm(EMPTY_FORM)}>
                                Reset
                            </Button>
                        </div>
                    </form>
                </Card>

                <Card>
                    <CardHeader
                        eyebrow='Periode akademik'
                        title='Daftar tahun ajaran'
                        action={<Badge tone='primary'>{groups.length} tahun ajaran</Badge>}
                    />

                    {groups.length === 0 ? (
                        <EmptyState
                            icon={<CalendarRange />}
                            title='Belum ada tahun ajaran'
                            description='Tambahkan periode pertama menggunakan formulir di sebelah kiri.'
                        />
                    ) : (
                        <div className='space-y-4 p-5'>
                            {groups.map(([name, semesters]) => (
                                <article key={name} className='overflow-hidden rounded-xl border'>
                                    <div className='flex items-center justify-between gap-3 border-b bg-muted/40 px-4 py-3'>
                                        <h3 className='text-base font-bold tracking-tight text-foreground'>
                                            {name}
                                        </h3>

                                        <span className='text-xs font-medium text-muted-foreground'>
                                            {semesters.length} semester
                                        </span>
                                    </div>

                                    <ul className='divide-y'>
                                        {semesters.map(year => (
                                            <li key={year.id} className={cn('flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center', year.active && 'bg-success/5')}>
                                                <div className='min-w-0 flex-1'>
                                                    <div className='flex flex-wrap items-center gap-2'>
                                                        <span className='text-sm font-semibold text-foreground'>
                                                            Semester {year.semester}
                                                        </span>

                                                        {year.active && <Badge tone='success' dot>Aktif</Badge>}
                                                    </div>

                                                    <p className='mt-1 flex items-center gap-1.5 text-xs text-muted-foreground'>
                                                        <CalendarDays className='size-3.5' aria-hidden='true' />

                                                        {year.startDate ? (
                                                            <>
                                                                {formatDate(year.startDate, 'medium')}
                                                                <ArrowRight className='size-3' aria-label='sampai' />
                                                                {year.endDate ? formatDate(year.endDate, 'medium') : 'Belum ditentukan'}
                                                            </>
                                                        ) : 'Tanggal periode belum diatur'}
                                                    </p>
                                                </div>

                                                <div className='flex flex-wrap gap-2'>
                                                    <Button variant='outline' size='xs' onClick={() => startEdit(year)}>
                                                        <Pencil aria-hidden='true' />
                                                        Ubah
                                                    </Button>

                                                    {!year.active && (
                                                        <>
                                                            <Button
                                                                variant='outline'
                                                                size='xs'
                                                                className='border-success/40 text-success hover:border-success/60 hover:bg-success/10 hover:text-success'
                                                                onClick={() => handleActivate(year)}
                                                            >
                                                                Jadikan aktif
                                                            </Button>

                                                            <Button
                                                                variant='danger-outline'
                                                                size='xs'
                                                                onClick={() => void handleDelete(year)}
                                                                aria-label={`Hapus semester ${year.semester} tahun ajaran ${year.name}`}
                                                            >
                                                                <Trash2 aria-hidden='true' />
                                                                Hapus
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </>
    )
}

export default SchoolYearsPage
